import { z } from 'zod';
import Wrapper from './Wrapper';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Stack, useToast } from '@chakra-ui/react';
import ZaraButton from 'components/custom-components/buttons/Button';
import { useChangeInitialMessageMutation } from 'graphql/generated';
import { useError } from 'error-provider/ErrorProvider';
import { useApolloClient } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { useGetAgent } from './hooks/useGetAgent';
import { useEffect } from 'react';
import Loader from './Loader';

const schema = z.object({
    greetings: z.string().min(1, 'Required'),
});

type Schema = z.infer<typeof schema>;

const AgentGreeting = () => {
    const { control, handleSubmit, setValue } = useForm<Schema>({
        defaultValues: {
            greetings: '',
        },
        mode: 'onSubmit',
        resolver: zodResolver(schema),
    });
    const { showError } = useError();
    const apolloClient = useApolloClient();
    const toast = useToast();
    const params = useParams();
    const { data, loading } = useGetAgent();

    const [changeGreetings, { loading: isChangeGreetingsLoading }] = useChangeInitialMessageMutation({
        onError(error) {
            showError(undefined, error);
        },
    });

    const onSubmit = (form: Schema) => {
        changeGreetings({
            variables: {
                initialMessage: form.greetings,
                agentId: params.id as string,
            },
            onCompleted(data) {
                if (data.changeInitialMessage.__typename === 'SuccessType') {
                    toast({
                        title: data.changeInitialMessage.message,
                        status: 'success',
                        isClosable: true,
                    });

                    apolloClient.cache.modify({
                        id: `AgentType:${params.id}`,
                        fields: {
                            initialMessage() {
                                return form.greetings;
                            },
                        },
                    });
                }

                if (data.changeInitialMessage.__typename === 'ErrorType') {
                    toast({
                        title: data.changeInitialMessage.message,
                        status: 'error',
                        isClosable: true,
                    });
                }
            },
        });
    };

    useEffect(() => {
        if (data?.initialMessage) setValue('greetings', data.initialMessage);
    }, [data, setValue]);

    if (loading || !data) {
        return <Loader />;
    }

    return (
        <Wrapper title='Agent Greeting' description={`Customize the initial greeting for your AI agent`}>
            <Stack as='form' onSubmit={handleSubmit(onSubmit)} alignItems='self-start'>
                <Controller
                    control={control}
                    name='greetings'
                    render={({ field: { onChange, onBlur, value } }) => (
                        <textarea
                            required
                            placeholder='Enter greeting guide here...'
                            className='flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 min-h-[200px] lg:min-h-[300px]'
                            autoFocus={true}
                            value={value}
                            onChange={onChange}
                            onBlur={onBlur}
                        />
                    )}
                />

                <ZaraButton isLoading={isChangeGreetingsLoading} type='submit' variant='primary' w='auto' fontSize='sm'>
                    Update
                </ZaraButton>
            </Stack>
        </Wrapper>
    );
};

export default AgentGreeting;
